import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  fetchFriends,
  sendFriendRequest,
  acceptFriendRequest,
  removeFriend,
  type Friend,
} from "@/lib/api";
import { isSessionEnding } from "@/lib/pending-saves";

interface FriendsContextValue {
  friends: Friend[];
  /** True until the first successful load (a failed first load stays loading, with `loadError`). */
  loading: boolean;
  /** The last refresh failed; `friends` is still the last list that loaded. */
  loadError: boolean;
  /** Refetch; resolves either way (failures only set `loadError`). Concurrent calls share one request. */
  refresh: () => Promise<void>;
  /** The actions throw on failure, after refreshing the list. */
  addFriend: (username: string) => Promise<void>;
  acceptFriend: (id: number) => Promise<void>;
  deleteFriend: (id: number) => Promise<void>;
}

const FriendsContext = createContext<FriendsContextValue | null>(null);

/**
 * The friends list, fetched at sign-in and again whenever the app comes back
 * to the foreground, Amigos opens or a friend action finishes, so requests and
 * acceptances from the other side show up without a reload. A failed refresh
 * keeps the previous list: dropping it would read as "no friends" and unmount
 * a live friend-mode route.
 */
export function FriendsProvider({ children }: { children: ReactNode }) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const inFlightRef = useRef<Promise<void> | null>(null);
  /** Only the latest request's answer is applied. */
  const seqRef = useRef(0);

  /** Always a new request (after an action, an older in-flight one may predate it). */
  const load = useCallback(() => {
    const seq = ++seqRef.current;
    const request: Promise<void> = fetchFriends()
      .then(
        (data) => {
          if (seq !== seqRef.current) return;
          setFriends(data);
          setLoading(false);
          setLoadError(false);
        },
        (error: unknown) => {
          console.error("Failed to load friends", error);
          if (seq === seqRef.current) setLoadError(true);
        },
      )
      .finally(() => {
        if (inFlightRef.current === request) inFlightRef.current = null;
      });
    inFlightRef.current = request;
    return request;
  }, []);

  const refresh = useCallback(() => inFlightRef.current ?? load(), [load]);

  useEffect(() => {
    void refresh();
    const onBack = () => {
      if (document.visibilityState === "visible" && !isSessionEnding()) void refresh();
    };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => {
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
    };
  }, [refresh]);

  /** Run a friend action, then refresh whether it worked or not (the other side may have changed it). */
  const act = useCallback(
    async (action: () => Promise<void>) => {
      try {
        await action();
      } finally {
        await load();
      }
    },
    [load],
  );

  const addFriend = useCallback((username: string) => act(() => sendFriendRequest(username)), [act]);
  const acceptFriend = useCallback((id: number) => act(() => acceptFriendRequest(id)), [act]);
  const deleteFriend = useCallback((id: number) => act(() => removeFriend(id)), [act]);

  return (
    <FriendsContext.Provider value={{ friends, loading, loadError, refresh, addFriend, acceptFriend, deleteFriend }}>
      {children}
    </FriendsContext.Provider>
  );
}

export function useFriends() {
  const ctx = useContext(FriendsContext);
  if (!ctx) throw new Error("useFriends must be used within FriendsProvider");
  return ctx;
}
